/**
 * InspectionDashboard — the main Phase 5 surface.
 *
 * Layout (desktop, ≥lg):
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Inspector chips row + zone dropdown row                     │
 *   ├──────────────┬────────────────────────┬─────────────────────┤
 *   │ DealSidebar  │ DashboardMap           │ DealDetailPanel     │
 *   │ (360px)      │ (flex-1)               │ (380px, collapsible)│
 *   └──────────────┴────────────────────────┴─────────────────────┘
 *
 * Layout (mobile, <lg): sidebar becomes bottom sheet; panel becomes bottom
 * sheet with snap points (handled inside DealDetailPanel).
 *
 * IMPORTANT: this whole shell is mounted ONCE per session (parent Dashboard
 * page owns the route). Filter changes never remount — they swap data refs.
 *
 * "Dock" pattern: closing the detail panel without clearing selection minimises
 * the deal into a small chip the user can re-expand. Lets staff hold multiple
 * deals open as drafts while triaging.
 */

import { useMemo, useState } from 'react';
import { Link2, MapPin as MapPinIcon, X, PanelRight, Users } from 'lucide-react';
import { clsx } from 'clsx';
import { useCrm } from '../../contexts/CrmContext';
import { useDealsWithSuburbs } from '../../hooks/useDealsWithSuburbs';
import { useLeadsWithSuburbs } from '../../hooks/useLeadsWithSuburbs';
import { useInspectors } from '../../hooks/useInspectors';
import { DashboardMap } from './DashboardMap';
import { DealSidebar } from './DealSidebar';
import { DealDetailPanel } from './DealDetailPanel';
import { LeadDetailPanel } from './LeadDetailPanel';
import { InspectorChips } from './InspectorChips';
import { RegionFilterMenu } from './RegionFilterMenu';
import { RightSidebar } from '../sidebar/RightSidebar';
import type { DealWithSuburb } from '../../hooks/useDealsWithSuburbs';
import type { LeadWithSuburb } from '../../hooks/useLeadsWithSuburbs';
import { LEAD_VIOLET } from '../../lib/leadDisplay';

export function InspectionDashboard() {
  const { status: crmStatus } = useCrm();
  const { inspectors, loading: inspectorsLoading } = useInspectors();
  const { items, loading, hasMore, loadMore, refresh, error } = useDealsWithSuburbs();
  const { items: leadItems, refetch: refetchLeads } = useLeadsWithSuburbs();

  const [selectedInspectorId, setSelectedInspectorId] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  // Leads layer toggle — on by default. State only; never remounts the shell.
  const [showLeads, setShowLeads] = useState(true);
  // Panel open vs minimised — distinct from selection so closing the panel
  // without clearing the dock keeps the deal selected (it gets a chip).
  const [panelOpen, setPanelOpen] = useState(false);
  const [dockedDealIds, setDockedDealIds] = useState<string[]>([]);
  // Right sidebar — mounts once, persists across filter changes.
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);

  /**
   * Apply the inspector + zone filters. Verdict + text search live inside
   * DealSidebar — they're list-only filters that don't need to leak up.
   */
  const visibleItems = useMemo<DealWithSuburb[]>(() => {
    return items.filter((item) => {
      if (selectedInspectorId) {
        if (item.assignedInspector?.id !== selectedInspectorId) return false;
      }
      if (selectedZone) {
        if (item.regionMatch?.zone !== selectedZone) return false;
      }
      return true;
    });
  }, [items, selectedInspectorId, selectedZone]);

  /**
   * Leads respect the zone filter when their region resolved (so filtering to a
   * zone narrows leads too). Leads without a region match stay visible only when
   * no zone filter is active — they have nowhere to be filtered into. No
   * inspector filter: leads are unassigned by definition.
   */
  const visibleLeads = useMemo<LeadWithSuburb[]>(() => {
    if (!selectedZone) return leadItems;
    return leadItems.filter((item) => item.regionMatch?.zone === selectedZone);
  }, [leadItems, selectedZone]);

  const selectedDeal = useMemo(
    () => (selectedDealId ? items.find((i) => i.deal.id === selectedDealId) ?? null : null),
    [items, selectedDealId],
  );

  const selectedLead = useMemo(
    () => (selectedLeadId ? leadItems.find((l) => l.lead.id === selectedLeadId) ?? null : null),
    [leadItems, selectedLeadId],
  );

  const dockedDeals = useMemo(
    () => dockedDealIds.map((id) => items.find((i) => i.deal.id === id)).filter((d): d is DealWithSuburb => !!d),
    [dockedDealIds, items],
  );

  const handleSelectDeal = (dealId: string | null) => {
    setSelectedDealId(dealId);
    setPanelOpen(!!dealId);
    if (dealId) {
      // Selecting a deal supersedes any open lead panel.
      setSelectedLeadId(null);
      // Promote out of the dock — it's now the active draft again.
      setDockedDealIds((prev) => prev.filter((id) => id !== dealId));
    }
  };

  const handleSelectLead = (leadId: string | null) => {
    setSelectedLeadId(leadId);
    if (leadId) {
      // A lead panel and a deal panel never share the dock — close the deal one.
      setSelectedDealId(null);
      setPanelOpen(false);
    }
  };

  const handleCloseLead = () => setSelectedLeadId(null);

  const handleLeadConverted = () => {
    // Lead became a deal — refresh both pools so it migrates across.
    void refetchLeads();
    void refresh();
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
    setSelectedDealId(null);
  };

  const handleMinimisePanel = () => {
    if (selectedDealId) {
      setDockedDealIds((prev) => (prev.includes(selectedDealId) ? prev : [...prev, selectedDealId]));
    }
    setPanelOpen(false);
    setSelectedDealId(null);
  };

  const handleRemoveFromDock = (id: string) => {
    setDockedDealIds((prev) => prev.filter((x) => x !== id));
  };

  // CRM-not-connected banner — short-circuit the layout entirely.
  if (crmStatus === 'unconfigured') {
    return (
      <div className="panel mt-8 border-amber-500/30 bg-amber-500/[0.04]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-amber-400">
              Setup required
            </div>
            <h2 className="mt-1 text-lg font-semibold">Connect your CRM to start routing leads.</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">
              Pick HubSpot Free or Attio, install the app, and we'll auto-route inbound deals to
              the right inspector based on your suburb-zone Sheet.
            </p>
          </div>
          <a href="#setup" className="btn-primary shrink-0">
            Start setup
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex h-[calc(100vh-160px)] min-h-[520px] flex-col gap-4">
      {/* Filter rail — inspector chips + zone dropdown. Mounted once,
          chips/zone selection re-renders only the chips themselves. */}
      <div className="space-y-3">
        <InspectorChips
          inspectors={inspectors}
          selectedInspectorId={selectedInspectorId}
          onSelect={setSelectedInspectorId}
          loading={inspectorsLoading}
        />
        <div className="flex flex-wrap items-center gap-2">
          <RegionFilterMenu selectedZone={selectedZone} onSelect={setSelectedZone} />
          <button
            type="button"
            onClick={() => setShowLeads((s) => !s)}
            aria-pressed={showLeads}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition',
              showLeads
                ? 'border-violet-400/40 text-violet-200'
                : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:bg-white/[0.06]',
            )}
            style={showLeads ? { backgroundColor: `${LEAD_VIOLET}1f` } : undefined}
          >
            <Users className="h-3 w-3" />
            Leads
            {leadItems.length > 0 && (
              <span className="tabular-nums opacity-70">{leadItems.length}</span>
            )}
          </button>
          {(selectedInspectorId || selectedZone) && (
            <button
              type="button"
              onClick={() => {
                setSelectedInspectorId(null);
                setSelectedZone(null);
              }}
              className="text-[11px] text-neutral-500 underline-offset-2 hover:text-neutral-300 hover:underline"
            >
              Clear filters
            </button>
          )}
          {error && (
            <span className="ml-auto rounded-full bg-red-500/10 px-2 py-1 text-[11px] text-red-300">
              {error}
            </span>
          )}
          <button
            type="button"
            onClick={() => setSidebarMobileOpen(true)}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-neutral-300 transition hover:bg-white/[0.08] lg:hidden"
            aria-label="Open calendar + email sidebar"
          >
            <PanelRight className="h-3 w-3" />
            <span>Workspace</span>
          </button>
        </div>
      </div>

      {/* Main split. min-h-0 is critical so the inner overflow scrollers
          actually scroll instead of pushing the layout. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:gap-4">
        {/* Sidebar — bottom half on mobile, left rail on desktop */}
        <div className="flex h-[320px] shrink-0 flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-3 lg:h-auto lg:w-[340px]">
          <DealSidebar
            items={visibleItems}
            leads={showLeads ? visibleLeads : []}
            selectedDealId={selectedDealId}
            selectedLeadId={selectedLeadId}
            onSelectDeal={handleSelectDeal}
            onSelectLead={handleSelectLead}
            onRefresh={refresh}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        </div>

        {/* Map — flex-1 always; the panel pushes it leftward when open. */}
        <div className="min-h-[300px] flex-1">
          <DashboardMap
            deals={visibleItems}
            leads={visibleLeads}
            showLeads={showLeads}
            inspectors={inspectors}
            selectedDealId={selectedDealId}
            selectedLeadId={selectedLeadId}
            onSelectDeal={handleSelectDeal}
            onSelectLead={handleSelectLead}
            loading={loading}
          />
        </div>

        {/* Right-docked deal panel (transient) */}
        <DealDetailPanel
          deal={selectedDeal}
          open={panelOpen && !!selectedDeal}
          onClose={handleClosePanel}
          onMinimise={handleMinimisePanel}
        />

        {/* Right-docked lead panel (transient) — mutually exclusive with deals */}
        <LeadDetailPanel
          lead={selectedLead}
          open={!!selectedLead}
          onClose={handleCloseLead}
          onConverted={handleLeadConverted}
        />

        {/* Right-docked calendar+email sidebar (always-on shell — mounts once) */}
        <RightSidebar
          mobileOpen={sidebarMobileOpen}
          onMobileOpenChange={setSidebarMobileOpen}
        />
      </div>

      {/* Bottom dock — chips for minimised deals. Click to re-open. */}
      {dockedDeals.length > 0 && (
        <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-2">
          <span className="shrink-0 text-[10px] font-medium uppercase tracking-widest text-neutral-500">
            Dock
          </span>
          {dockedDeals.map((item) => (
            <div
              key={item.deal.id}
              className="group flex shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] pl-2 pr-1 text-xs text-neutral-300"
            >
              <button
                type="button"
                onClick={() => handleSelectDeal(item.deal.id)}
                className="flex items-center gap-1.5 py-1 pr-1 hover:text-white"
              >
                {item.regionMatch?.zone ? (
                  <MapPinIcon className="h-3 w-3 text-neutral-500" />
                ) : (
                  <Link2 className="h-3 w-3 text-neutral-500" />
                )}
                <span className="max-w-[160px] truncate">{item.deal.name}</span>
              </button>
              <button
                type="button"
                onClick={() => handleRemoveFromDock(item.deal.id)}
                className={clsx(
                  'rounded-full p-0.5 text-neutral-600 hover:bg-white/10 hover:text-neutral-300',
                )}
                aria-label="Remove from dock"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
